import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Tag,
  Space,
  InputNumber,
  message,
  Badge,
  Tooltip,
  Divider,
  Select,
  Input,
  Slider,
  Collapse,
  Spin,
  Pagination
} from 'antd';
import {
  ShoppingCartOutlined,
  PlusOutlined,
  WifiOutlined,
  CalendarOutlined,
  FilterOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { packService, Pack, getPackAgencyPrice,getPackPrice, getPackCurrencySymbol, convertDataValue } from '../../services/packService';
import { cartService } from '../../services/cartService';
import { useHistory } from 'react-router-dom';
import styles from './Packs.module.css';
import { useSearch } from '../../contexts/SearchContext';
import LazyPackImage from '../../components/LazyPackImage/LazyPackImage';
import ReactCountryFlag from 'react-country-flag';
import getCountryISO2 from 'country-iso-3-to-2';
import styles1 from '../../pages/admin/Subscribers.module.css';
import { useTranslation } from 'react-i18next';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const { Search } = Input;

interface FilterState {
  dataRange: [number, number];
  durationRange: [number, number];
  priceRange: [number, number];
  status: string;
  searchText: string;
}

const AgencyPacks: React.FC = () => {
  const { t } = useTranslation();
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});
  const [cartCount, setCartCount] = useState(0);
  const [filters, setFilters] = useState<FilterState>({
    dataRange: [0, 100],
    durationRange: [1, 365],
    priceRange: [0, 200],
    status: undefined,
    searchText: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const history = useHistory();
  const { searchTerm } = useSearch();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const [dataLimits, setDataLimits] = useState<[number, number]>([0, 100]);
  const [priceLimits, setPriceLimits] = useState<[number, number]>([0, 200]);

  const fetchPacks = useCallback(async (
    page = 1,
    pageSize = 20,
    backendFilters?: {
      name?: string;
      status?: string;
      priceMin?: number;
      priceMax?: number;
      dataValueMin?: number;
      dataValueMax?: number;
      validityDurationMin?: number;
      validityDurationMax?: number;
    }
  ) => {
    try {
      setLoading(true);

      const response = await packService.getAllAdmin({
        page: page - 1,
        size: pageSize,
        filters: backendFilters
      });

      setPacks(response.data.content);

      setPagination({
        current: page,
        pageSize,
        total: response.data.totalElements
      });

      const initialQuantities: { [key: number]: number } = {};
      response.data.content.forEach((pack: Pack) => {
        initialQuantities[pack.id] = 1;
      });
      setQuantities(initialQuantities);
    } catch (error) {
      message.error(t('agency.packs.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const [appliedBackendFilters, setAppliedBackendFilters] = useState<any>({});

  const applyFilters = () => {
    const backendFilters = {
      name: filters.searchText || undefined,
      status: filters.status || undefined,
      priceMin: filters.priceRange[0] || undefined,
      priceMax: filters.priceRange[1] || undefined,
      dataValueMin: filters.dataRange[0] * 1024 * 1024 || undefined,
      dataValueMax: filters.dataRange[1] * 1024 * 1024 || undefined,
      validityDurationMin: filters.durationRange[0] || undefined,
      validityDurationMax: filters.durationRange[1] || undefined
    };

    setAppliedBackendFilters(backendFilters);

    fetchPacks(1, pagination.pageSize, backendFilters); 
  };

  useEffect(() => {
    fetchPacks();
    updateCartCount();
  }, [fetchPacks]);



  
  


  const updateCartCount = () => {
    const cart = cartService.getCart();
    setCartCount(cart.items.length);
  };

  // Calculer les valeurs min/max 
  const durationLimits = useMemo(() => {
    return [1, 365];
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await packService.getStats();
        const stats = response.data;
        
        const minData = stats.minData != null ? Math.round(stats.minData / (1024 * 1024)) : 0;
        const maxData = stats.maxData != null ? Math.round(stats.maxData / (1024 * 1024)) : 100;

        setDataLimits([minData, maxData]);

        const minPrice = stats.minPrice != null ? Number(stats.minPrice) : 0;
        const maxPrice = stats.maxPrice != null ? Number(stats.maxPrice) : 200;

        setPriceLimits([minPrice, maxPrice]);
        
      } catch (error) {
        console.error('Erreur lors de la récupération des stats des packs', error);
      }
    };

    fetchStats();
  }, []);


  // Mettre à jour les limites des filtres quand les packs changent
  useEffect(() => {
    if (packs.length > 0) {
      setFilters(prev => ({
        ...prev,
        dataRange: [dataLimits[0], dataLimits[1]] as [number, number],
        durationRange: [durationLimits[0], durationLimits[1]] as [number, number],
        priceRange: [priceLimits[0], priceLimits[1]] as [number, number]
      }));
    }
  }, [dataLimits, durationLimits, priceLimits, packs.length]);

  const filteredPacks = packs;

  // Fonctions de gestion des filtres
  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      dataRange: [dataLimits[0], dataLimits[1]] as [number, number],
      durationRange: [durationLimits[0], durationLimits[1]] as [number, number],
      priceRange: [priceLimits[0], priceLimits[1]] as [number, number],
      status: undefined,
      searchText: ''
    });
    fetchPacks();
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleQuantityChange = (packId: number, quantity: number) => {
    setQuantities({
      ...quantities,
      [packId]: quantity
    });
  };

  const addToCart = (pack: Pack) => {
    try {
      const quantity = quantities[pack.id] || 1;
      
      cartService.addItem({
        packId: pack.id,
        packName: pack.name,
        quantity,
        unitPrice: getPackAgencyPrice(pack)
      });
      
      message.success(t('agency.packs.addToCartSuccess', { quantity, name: pack.name }));
      updateCartCount();
    } catch (error) {
      message.error(t('agency.packs.addToCartError'));
      console.error(error);
    }
  };

  const viewCart = () => {
    history.push('/agency/cart');
  };

  const renderFilterPanel = () => (
    <Card className={styles.filterCard}>
      <Collapse activeKey={showFilters ? ['1'] : []} onChange={() => setShowFilters(!showFilters)}>
        <Panel 
          header={
            <Space>
              <FilterOutlined />
              <span>{t('agency.packs.filters')}</span>
            </Space>
          } 
          key="1"
          extra={
            <Button 
              size="small" 
              icon={<ClearOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                clearFilters();
              }}
            >
              {t('agency.packs.clearFilters')}
            </Button>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={14}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {t('agency.packs.searchLabel')}
                </Text>
                

                <Input
                  placeholder={t('agency.packs.searchPlaceholder')}
                  value={filters.searchText}
                  onChange={(e) =>
                    handleFilterChange('searchText', e.target.value)
                  }
                  allowClear
                  style={{ width: 500 }}
                  className={styles1.customInput}
                />
                </Col>

  <Col xs={12} md={12}>
  <Text strong style={{ display: 'block', marginBottom: 8, }}>
                  {t('agency.packs.statusLabel')}
                </Text>
                <Select
                  className={styles1.customSelect}
                  placeholder={t('agency.packs.statusLabel')}
                  value={filters.status}
                  onChange={(val) =>
                    setFilters((prev) => ({ ...prev, status: val }))
                  }
                  allowClear
                  style={{ width: 350}}
                  // className={styles.customSelect}
                >
                  <Option value="available">{t('agency.packs.statusAvailable')}</Option>
                  <Option value="unavailable">{t('agency.packs.statusUnavailable')}</Option>
                </Select>
              
            </Col>
                </Row>
<Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={4}>
              <Space direction="vertical" className={styles.filterColumn}>
                <Text strong>{t('agency.packs.dataLabel')}</Text>
                <Text type="secondary" className={styles.filterLabel}>
                  {filters.dataRange[0]} - {filters.dataRange[1]} Go
                </Text>
                <Slider
                  range
                  min={dataLimits[0]}
                  max={dataLimits[1]}
                  value={filters.dataRange}
                  onChange={(value) => handleFilterChange('dataRange', value)}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <Space direction="vertical" className={styles.filterColumn}>
                <Text strong>{t('agency.packs.durationLabel')}</Text>
                <Text type="secondary" className={styles.filterLabel}>
                  {filters.durationRange[0]} - {filters.durationRange[1]} {t('common.days')}
                </Text>
                <Slider
                  range
                  min={durationLimits[0]}
                  max={durationLimits[1]}
                  value={filters.durationRange}
                  onChange={(value) => handleFilterChange('durationRange', value)}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <Space direction="vertical" className={styles.filterColumn}>
                <Text strong>{t('agency.packs.priceLabel')}</Text>
                <Text type="secondary" className={styles.filterLabel}>
                  {filters.priceRange[0]}€ - {filters.priceRange[1]}€
                </Text>
                <Slider
                  range
                  min={priceLimits[0]}
                  max={priceLimits[1]}
                  value={filters.priceRange}
                  onChange={(value) => handleFilterChange('priceRange', value)}
                />
              </Space>
            </Col>

            <Button style={{ marginTop: 40, }} type="primary" onClick={applyFilters}>{t('agency.packs.applyFilters')}</Button>

          </Row>
        </Panel>
      </Collapse>
    </Card>
  );

  const renderPackCard = (pack: Pack) => {
    const price=getPackAgencyPrice(pack) ?? 0;
    console.log('price',price);
    //if(price!==0)
    return (
      <Col xs={24} sm={12} md={8} lg={6} key={pack.id}>
        <Card
          loading={loading}
          hoverable
          className={styles.packCard}
          cover={
            <LazyPackImage productId={pack.productId} packName={pack.name}>
              <div className={styles.packCoverContent}>
                <div className={styles.packCoverOverlay}>
                  <Title color='white' level={2} className={styles.packTitle} style={{ color: 'white' }}>
                    {price} {getPackCurrencySymbol(pack)}
                  </Title>
                  {pack.status === 'available' ? (
                    <Tag color="success" className={styles.packTag}>{t('agency.packs.tagActive')}</Tag>
                  ) : (
                    <Tag color="error" className={styles.packTag}>{t('agency.packs.tagInactive')}</Tag>
                  )}
                   
                </div>
                
              </div>
            </LazyPackImage>
          }
        >
        <div className={styles.packContent}>
          <Title level={4}>{pack.name}</Title>
          <>
            {pack.countries?.map(country => (
              
              <ReactCountryFlag key={country.code} countryCode={getCountryISO2(country.code) } svg style={{ width: '1.5em', height: '1.5em', borderRadius: '100%' }} />
              
            ))}
          </>
          {pack.description && (
            <Paragraph ellipsis={{ rows: 2 }}>
              {pack.description || ''}
            </Paragraph>
          )}

          <Space direction="vertical" className={styles.packInfo}>
        <Space>
          <CalendarOutlined />
          <Text>{pack.validityDuration} {pack.validityDurationUnit === 'days' ? t('common.days') : pack.validityDurationUnit === 'months' ? t('common.months') : t('common.years')}</Text>
        </Space>
        <Space>
          <WifiOutlined />
          <Text>{convertDataValue(pack).value} {convertDataValue(pack).unit} {t('agency.packs.dataUnit')}</Text>
        </Space>
          </Space>
        </div>
        
        <Divider className={styles.packDivider} />
        
        <div>
          <Space className={styles.packActions}>
        <InputNumber
          min={1}
          max={100}
          value={quantities[pack.id] || 1}
          onChange={(value) => handleQuantityChange(pack.id, value as number)}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => addToCart(pack)}
          disabled={pack.status !== "available"}
        >
          {t('agency.packs.addToCart')}
        </Button>
          </Space>
        </div>
      </Card>
    </Col>
    );
  };

  return (
    <>
      <div className={styles.packsHeader}>
        <Title level={3}>{t('agency.packs.availablePacks')}</Title>
        <Space>
          <Button 
            icon={<FilterOutlined />} 
            onClick={toggleFilters}
            type={showFilters ? 'primary' : 'default'}
          >
            {t('agency.packs.filters')}
          </Button>
          <Tooltip title={t('agency.packs.viewCart')}>
            <Badge count={cartCount}>
              <Button
                type="primary"
                size="large"
                icon={<ShoppingCartOutlined />}
                onClick={viewCart}
              >
                {t('agency.packs.cartLabel')}
              </Button>
            </Badge>
          </Tooltip>
        </Space>
      </div>
      
      {renderFilterPanel()}
      {loading? <> <Spin /> </> :<></> }
      <br>
      </br>
      {filteredPacks.length === 0 && !loading ? (
        <Card>
          <div className={styles.emptyState}>
            <Text type="secondary">{t('agency.packs.noResults')}</Text>
            <br />
            <Button type="link" onClick={clearFilters}>
              {t('agency.packs.clearFilters')}
            </Button>
          </div>
        </Card>
      ) : (
        <>
        <Row gutter={[16, 16]}>
          {filteredPacks.map(renderPackCard)}
        </Row>
        
        {/* Pagination */}
        <Row justify="center" style={{ marginTop: 32 }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            pageSizeOptions={['8', '16', '24', '32']}
            onChange={(page, pageSize) => {
              fetchPacks(page, pageSize, appliedBackendFilters);
            }}
          />
        </Row>
        </>
      )}
    </>
  );
};

export default AgencyPacks;
